// src/App.js
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { NotificationProvider } from "./context/NotificationContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";
import { TimeTrackingProvider } from "./context/TimeTrackingContext";
import { RoutePageSkeleton } from "./components/Skeletons";

import AuthScreen from "./pages/AuthScreen";
import ResetPassword from "./pages/ResetPassword";
import HomeRedirect from "./components/HomeRedirect";
import ZiloLockGate from "./components/ZiloLockGate";
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Overview = lazy(() => import("./pages/Overview"));
const Plans = lazy(() => import("./pages/Plans"));
const Support = lazy(() => import("./pages/Support"));
const Clients = lazy(() => import("./pages/Clients"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const Settings = lazy(() => import("./pages/Settings"));
const Billing = lazy(() => import("./pages/Billing"));
const WebDevelopment = lazy(() => import("./pages/WebDevelopment"));
const EmailMarketing = lazy(() => import("./pages/EmailMarketing"));
const GoogleGrowth = lazy(() => import("./pages/GoogleGrowth"));
const WebsiteManagement = lazy(() => import("./pages/WebsiteManagement"));
const PublicFeedback = lazy(() => import("./pages/PublicFeedback"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const BookingManage = lazy(() => import("./pages/BookingManage"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PackageOnboarding = lazy(() => import("./pages/PackageOnboarding"));

// Admin pages are lazy-loaded — regular business users never download this
// code at all. AdminLeads specifically pulls in the xlsx library (~110KB
// gzipped) for Excel import, which has no business being in every
// business's initial bundle just because one admin page needs it.
const AdminOverview   = lazy(() => import("./pages/admin/AdminOverview"));
const AdminBusinesses = lazy(() => import("./pages/admin/AdminBusinesses"));
const AdminReviews    = lazy(() => import("./pages/admin/AdminReviews"));
const AdminTestChat   = lazy(() => import("./pages/admin/AdminTestChat"));
const AdminCosts      = lazy(() => import("./pages/admin/AdminCosts"));
const AdminCampaigns  = lazy(() => import("./pages/admin/AdminCampaigns"));
const AdminWebsiteManagement = lazy(() => import("./pages/admin/AdminWebsiteManagement"));
const AdminWebsiteBuilder = lazy(() => import("./pages/admin/AdminWebsiteBuilder"));
const AdminBlogs = lazy(() => import("./pages/admin/AdminBlogs"));
const AdminGooglePosts = lazy(() => import("./pages/admin/AdminGooglePosts"));
const AdminReviewWidgets = lazy(() => import("./pages/admin/AdminReviewWidgets"));
const AdminLeads      = lazy(() => import("./pages/admin/AdminLeads"));
const AdminAuditLog   = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminTasks      = lazy(() => import("./pages/admin/AdminTasks"));
const AdminTimeTracker = lazy(() => import("./pages/admin/AdminTimeTracker"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminProfile    = lazy(() => import("./pages/admin/AdminProfile"));
const AdminTeam       = lazy(() => import("./pages/admin/AdminTeam"));
const AdminPayments   = lazy(() => import("./pages/admin/AdminPayments"));

// Which layout to draw is decided by the URL, so each page loads into a
// placeholder shaped like itself.
function AdminPageFallback() {
  return <RoutePageSkeleton pathname={useLocation().pathname} />;
}

function PageFallback() {
  return <RoutePageSkeleton pathname={useLocation().pathname} />;
}

export default function App() {
  return (
    <ThemeProvider>
      <NotificationProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/login"  element={<AuthScreen />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup" element={<AuthScreen />} />
            <Route path="/onboarding" element={<Suspense fallback={<PageFallback />}><Onboarding /></Suspense>} />
            <Route path="/checkout" element={<Suspense fallback={<PageFallback />}><Checkout /></Suspense>} />
            <Route path="/feedback/:campaignId" element={<Suspense fallback={<PageFallback />}><PublicFeedback /></Suspense>} />
            <Route path="/book/:slug" element={<Suspense fallback={<PageFallback />}><BookingPage /></Suspense>} />
            <Route path="/booking/:id" element={<Suspense fallback={<PageFallback />}><BookingManage /></Suspense>} />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <TimeTrackingProvider>
                    <AdminLayout />
                  </TimeTrackingProvider>
                </AdminRoute>
              }
            >
              <Route index        element={<AdminRoute require="overview"><Suspense fallback={<AdminPageFallback />}><AdminOverview /></Suspense></AdminRoute>} />
              <Route path="leads"      element={<AdminRoute require="leads"><Suspense fallback={<AdminPageFallback />}><AdminLeads /></Suspense></AdminRoute>} />
              <Route path="businesses" element={<AdminRoute require="businesses"><Suspense fallback={<AdminPageFallback />}><AdminBusinesses /></Suspense></AdminRoute>} />
              <Route path="website"    element={<AdminRoute require="website"><Suspense fallback={<AdminPageFallback />}><AdminWebsiteManagement /></Suspense></AdminRoute>} />
              <Route path="website-builder" element={<AdminRoute require="website"><Suspense fallback={<AdminPageFallback />}><AdminWebsiteBuilder /></Suspense></AdminRoute>} />
              <Route path="blogs"      element={<AdminRoute require="website"><Suspense fallback={<AdminPageFallback />}><AdminBlogs /></Suspense></AdminRoute>} />
              <Route path="google-posts" element={<AdminRoute require="google"><Suspense fallback={<AdminPageFallback />}><AdminGooglePosts /></Suspense></AdminRoute>} />
              <Route path="review-widgets" element={<AdminRoute require="reviews"><Suspense fallback={<AdminPageFallback />}><AdminReviewWidgets /></Suspense></AdminRoute>} />
              <Route path="reviews"    element={<AdminRoute require="reviews"><Suspense fallback={<AdminPageFallback />}><AdminReviews /></Suspense></AdminRoute>} />
              <Route path="test-chat"  element={<AdminRoute require="testChat"><Suspense fallback={<AdminPageFallback />}><AdminTestChat /></Suspense></AdminRoute>} />
              <Route path="costs"      element={<AdminRoute superAdminOnly><Suspense fallback={<AdminPageFallback />}><AdminCosts /></Suspense></AdminRoute>} />
              <Route path="campaigns"  element={<AdminRoute require="campaigns"><Suspense fallback={<AdminPageFallback />}><AdminCampaigns /></Suspense></AdminRoute>} />
              <Route path="audit-log"  element={<AdminRoute require="auditLog"><Suspense fallback={<AdminPageFallback />}><AdminAuditLog /></Suspense></AdminRoute>} />
              <Route path="tasks"      element={<AdminRoute><Suspense fallback={<AdminPageFallback />}><AdminTasks /></Suspense></AdminRoute>} />
              <Route path="time"       element={<AdminRoute><Suspense fallback={<AdminPageFallback />}><AdminTimeTracker /></Suspense></AdminRoute>} />
              <Route path="bookings"   element={<AdminRoute><Suspense fallback={<AdminPageFallback />}><AdminBookings /></Suspense></AdminRoute>} />
              <Route path="profile"    element={<AdminRoute><Suspense fallback={<AdminPageFallback />}><AdminProfile /></Suspense></AdminRoute>} />
              <Route path="team"       element={<AdminRoute superAdminOnly><Suspense fallback={<AdminPageFallback />}><AdminTeam /></Suspense></AdminRoute>} />
              <Route path="payments"   element={<AdminRoute superAdminOnly><Suspense fallback={<AdminPageFallback />}><AdminPayments /></Suspense></AdminRoute>} />
            </Route>

            {/* Business routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<HomeRedirect />} />
              <Route path="overview"  element={<Suspense fallback={<PageFallback />}><Overview /></Suspense>} />
              <Route path="dashboard"  element={<Suspense fallback={<PageFallback />}><ZiloLockGate><Dashboard /></ZiloLockGate></Suspense>} />
              <Route path="pricing"  element={<Suspense fallback={<PageFallback />}><Plans /></Suspense>} />
              <Route path="support"  element={<Suspense fallback={<PageFallback />}><Support /></Suspense>} />
              <Route path="clients"    element={<Suspense fallback={<PageFallback />}><ZiloLockGate><Clients /></ZiloLockGate></Suspense>} />
              <Route path="reviews"    element={<Suspense fallback={<PageFallback />}><ZiloLockGate><Reviews /></ZiloLockGate></Suspense>} />
              <Route path="campaigns"  element={<Suspense fallback={<PageFallback />}><ZiloLockGate><Campaigns /></ZiloLockGate></Suspense>} />
              <Route path="settings"   element={<Suspense fallback={<PageFallback />}><Settings /></Suspense>} />
              <Route path="billing" element={<Suspense fallback={<PageFallback />}><Billing /></Suspense>} />
              <Route path="web-development" element={<Suspense fallback={<PageFallback />}><WebDevelopment /></Suspense>} />
              <Route path="package-onboarding" element={<Suspense fallback={<PageFallback />}><PackageOnboarding /></Suspense>} />
              <Route path="email-marketing" element={<Suspense fallback={<PageFallback />}><EmailMarketing /></Suspense>} />
              <Route path="google-growth" element={<Suspense fallback={<PageFallback />}><GoogleGrowth /></Suspense>} />
              <Route path="website-management" element={<Suspense fallback={<PageFallback />}><WebsiteManagement /></Suspense>} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </NotificationProvider>
    </ThemeProvider>
  );
}
